import { useEffect, useState } from 'react'
import { Dialog, Box } from '@mui/material'
import { useSelector } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'

import { getRubric, useRubric } from '../../hooks/useRubrics'
import useUtils from '../../hooks/useUtilities'

import RubricAppBar from './RubricAppBar'
import Sidebar from './Sidebar'
import Editor from './Editor'
import GenerateRubric from './GenerateRubric'

import { trackRubricCreated, trackRubricUsed } from '@/lib/intercom_events'
import usePermissions from '@/hooks/usePermissions'
import { useBuilderStore, useBuilderStoreActions } from '@/views/NewBuilder/hooks/builder/useBuilderStore'

let timeout = null

function RubricDialog({
  open,
  onClose,
  onSave = () => {},
  onSaveToAll = () => {},
  onSetDefaultRubric = () => {},
  onUpdateRubric = () => {},
  onDelete = () => {},
  rubricId,
  tab: defaultTab = null,
  context = {},
  isActivitySettings = false,
  isOnlyOneSave = false,
}) {
  const { showSnackbar } = useUtils()
  const queryClient = useQueryClient()
  const { plan } = usePermissions()
  const uid = useSelector(({ user }) => user.auth.uid)
  const isFormOpen = useBuilderStore(store => store.isFormOpen)
  const { updateCardField } = useBuilderStoreActions()

  const [tab, setTab] = useState('library')
  const [saving, setSaving] = useState(false)
  const [rubric, setRubric] = useState(null)
  const [loadingRubric, setLoadingRubric] = useState(false)
  const [copyingRubric, setCopyingRubric] = useState(false)
  const { updateRubric, createRubric, deleteRubric, copyRubric } = useRubric({
    id: rubric?.id,
    enabled: !!rubric?.id,
  })
  const fetchRubric = async id => {
    const rubric = await getRubric(id)

    setRubric(rubric)
  }

  useEffect(() => {
    if (rubricId) {
      fetchRubric(rubricId)
    } else {
      setRubric(null)
    }
  }, [rubricId, open])
  useEffect(() => {
    if (defaultTab) {
      setTab(defaultTab)
    }
  }, [defaultTab])

  const createNewRubric = async (r = {}) => {
    const total_points = getTotalPoints(r)
    const rubric = {
      name: '',
      format: 'table', // or 'paragraph'
      criteria: [
        {
          criterion_id: `criterion_0`,
          description: '',
          title: '',
          score_definitions: [
            {
              score_id: 'score_0',
              level: 0,
              definition: '',
            },
            {
              score_id: 'score_1',
              level: 1,
              definition: '',
            },
          ],
        },
      ],
      score_titles: {},
      total_points: total_points,
      ...r,
    }
    const newRubric = await createRubric(rubric)

    trackRubricCreated({
      rubricId: newRubric.id,
      userId: uid,
      plan: plan,
    })

    setRubric({
      ...rubric,
      ...newRubric,
    })
    setTab('editor')
    queryClient.invalidateQueries({
      queryKey: ['rubrics'],
    })
  }

  const getTotalPoints = newRubric => {
    if (!newRubric || !Object.keys(newRubric).length) return 1
    let total = 0
    const rubric = newRubric || rubric

    rubric.criteria.forEach(criterion => {
      if (!criterion.score_definitions.length) return
      const levels = criterion.score_definitions.map(s => s.level)
      const maxLevel = Math.max(...levels)

      total += maxLevel
    })

    return total
  }

  const handleSave = async saveToAll => {
    const { isError, message } = validateRubric(rubric)

    if (isError) {
      showSnackbar(message, 'error')

      return
    }

    if (isOnlyOneSave) {
      onSave(rubric.id || rubric, rubric)

      trackRubricUsed({
        rubricId: rubric.id,
        userId: uid,
        plan: plan,
      })

      return
    }

    if (isFormOpen) {
      updateCardField('rubricId', rubric.id || rubric)
    } else {
      const passing_score = Math.floor((rubric?.total_points || 0) / 2)

      if (saveToAll) {
        onSaveToAll(rubric.id, passing_score, rubric?.total_points)
      } else {
        onSave(rubric.id || rubric, passing_score, rubric?.total_points)
      }
      trackRubricUsed({
        rubricId: rubric.id,
        userId: uid,
        plan: plan,
      })
    }
    handleClose()
  }

  const deleteRubricHandler = async () => {
    if (window.confirm('Are you sure you want to delete this rubric?')) {
      await deleteRubric(rubric.id)
      setRubric(null)
      onDelete()
    }

    return
  }

  const onChangeTab = tab => {
    setTab(tab)
  }

  const handleUpdateRubric = (updates = {}) => {
    const newRubric = {
      ...rubric,
      ...updates,
    }
    const total_points = getTotalPoints(newRubric)

    newRubric.total_points = total_points
    setRubric(newRubric)
    clearTimeout(timeout)

    timeout = setTimeout(async () => {
      setSaving(true)

      await updateRubric({
        rubricId: rubric.id,
        updates: {
          ...updates,
          total_points: total_points,
        },
      })
      onUpdateRubric({ rubricId: rubric.id, total_points })
      setSaving(false)
    }, 1500)
  }

  const handleCopyRubric = async rubricId => {
    setCopyingRubric(true)
    try {
      const resultId = await copyRubric(rubricId)

      await fetchRubric(resultId)
      showSnackbar('Rubric copied successfully')
      queryClient.invalidateQueries({
        queryKey: ['rubrics'],
      })
    } catch (e) {
      console.error('handleCopyRubric', e)
    } finally {
      setCopyingRubric(false)
    }
  }

  const handleClose = () => {
    clearTimeout(timeout)
    queryClient.removeQueries({
      queryKey: ['rubric'],
    })
    setRubric(null)
    setTab('library')
    onClose()
  }

  return (
    <Dialog
      fullScreen
      fullWidth
      open={open}
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: 'background.default',
        },
      }}
      onClick={e => {
        e.preventDefault()
        e.stopPropagation()
      }}
      onClose={handleClose}
    >
      <RubricAppBar
        handleCreateRubric={() => {
          setRubric(null)
          setTab('new')
        }}
        handleSave={handleSave}
        handleSetAsDefault={() => {
          onSetDefaultRubric(rubric.id, rubric?.name)
        }}
        isActivitySettings={isActivitySettings}
        isOnlyOneSave={isOnlyOneSave}
        rubric={rubric}
        saving={saving}
        tab={tab}
        onClose={handleClose}
      />
      {/* Create a side navigation panel */}
      <Sidebar
        loadingRubric={loadingRubric}
        rubric={rubric}
        selectedRubricId={rubric?.id}
        setLoadingRubric={setLoadingRubric}
        tab={tab}
        onChangeTab={onChangeTab}
        onSelectRubric={setRubric}
        onUpdateRubric={handleUpdateRubric}
      />
      <Box
        sx={{
          p: `22px 32px 32px ${tab === 'new' ? 112 : 512}px`,
          width: '100%',
        }}
      >
        {(tab === 'editor' || tab === 'library') && rubric ? (
          <Editor
            copyRubric={handleCopyRubric}
            copyingRubric={copyingRubric}
            editing={tab === 'editor'}
            handleDelete={deleteRubricHandler}
            handleEditRubric={async () => {
              if (rubric?.owners?.includes?.(uid)) {
                setTab('editor')
              } else {
                const resultId = await copyRubric(rubric?.id)

                await fetchRubric(resultId)
                showSnackbar('Rubric copied successfully')
                queryClient.invalidateQueries({
                  queryKey: ['rubrics'],
                })
                setTab('editor')
              }
            }}
            isOwner={rubric?.owners?.includes?.(uid)}
            loading={loadingRubric}
            rubric={rubric}
            setLoadingRubric={setLoadingRubric}
            setRubric={setRubric}
            onUpdateRubric={handleUpdateRubric}
          />
        ) : null}
        {/* {tab === "library" && <Preview rubric={rubric} />} */}
        {tab === 'new' && (
          <GenerateRubric
            context={context}
            loadingRubric={loadingRubric}
            setLoadingRubric={setLoadingRubric}
            onCreateFromScratch={() => createNewRubric()}
            onGenerateRubric={createNewRubric}
          />
        )}
      </Box>
    </Dialog>
  )
}

// The CriteriaList component would be defined here along with other subcomponents.

export default RubricDialog

const validateRubric = rubric => {
  if (!rubric.criteria.length) {
    return {
      isError: true,
      message: 'Please add at least one criterion to save the rubric',
    }
  } else if (!rubric.criteria.every(c => c.score_definitions.length)) {
    return {
      isError: true,
      message: 'Please add at least one score definition to each criterion to save the rubric',
    }
  }
  // if there is a criteria, make sure there is a title and description
  else if (!rubric.criteria.every(c => c.title && c.description)) {
    return {
      isError: true,
      message: 'Please add a title and description to each criterion to save the rubric',
    }
  }
  // if there are score definitions, make sure there is a definition
  else if (!rubric.criteria.every(c => c.score_definitions.every(s => s.definition))) {
    return {
      isError: true,
      message: 'Please add a definition to each score definition to save the rubric',
    }
  } else if (!rubric.name) {
    return {
      isError: true,
      message: 'Please add a name to the rubric',
    }
  } else if (rubric.criteria.length !== new Set(rubric.criteria.map(c => c.title)).size) {
    return {
      isError: true,
      message: 'Please ensure that each criterion has a unique title',
    }
  }

  return {
    isError: false,
    message: '',
  }
}
